import { useState, type FormEvent } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft, Loader2, MailCheck } from 'lucide-react';

import { AuthShell, FormNotice } from '@/components/auth-shell';
import { Button, Input } from '@/components/ui';
import { requestPasswordReset } from '@/lib/account-api';
import { errorMessage } from '@/lib/utils';

export function ForgotPasswordPage() {
  const location = useLocation();
  // Carried over from the login form so the address is not typed twice.
  const [email, setEmail] = useState((location.state as { email?: string } | null)?.email ?? '');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);

    if (!email.trim()) {
      setError('Enter the email address you sign in with.');
      return;
    }

    setSubmitting(true);

    try {
      await requestPasswordReset(email.trim());
      setSent(true);
    } catch (cause) {
      // Validation and rate-limit errors only. An unknown address succeeds,
      // so nothing here can reveal whether an account exists.
      setError(errorMessage(cause, 'Could not send the reset link. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell subtitle="Reset your password">
      {sent ? (
        <div className="space-y-4 text-center">
          <MailCheck className="mx-auto size-10 text-success-600" aria-hidden="true" />
          <div role="status">
            <p className="font-medium text-ink">Check your inbox</p>
            <p className="mt-1 text-sm text-ink-muted">
              If an account exists for <span className="font-medium text-ink">{email.trim()}</span>,
              we have sent a link to reset its password. The email says how long the link stays valid.
            </p>
          </div>
          <Button variant="outline" fullWidth onClick={() => setSent(false)}>
            Use a different address
          </Button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          <p className="text-sm text-ink-muted">
            Enter the email you sign in with and we will send you a link to choose a new password.
          </p>

          {error && <FormNotice tone="danger">{error}</FormNotice>}

          <div>
            <label htmlFor="email" className="mb-1 block text-sm font-medium text-ink">
              Email
            </label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
              autoComplete="username"
              autoFocus
            />
          </div>

          <Button type="submit" fullWidth disabled={submitting}>
            {submitting && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
            {submitting ? 'Sending…' : 'Send reset link'}
          </Button>
        </form>
      )}

      <Link
        to="/login"
        className="mt-5 flex items-center justify-center gap-1.5 text-sm font-medium text-brand-600 hover:underline"
      >
        <ArrowLeft className="size-4" aria-hidden="true" />
        Back to sign in
      </Link>
    </AuthShell>
  );
}
